import React, { createContext, useCallback, useContext, useMemo, useState } from 'react';
import { STORAGE_KEYS } from 'constants/storageKeys';
import { ROUTES } from 'config/routes';
import { getResolvedTenantCode, setResolvedTenant } from 'config/resolvedTenant';
import storeAdminAuthService from 'services/admin/storeAdminAuthService';
import { tenantStorage } from 'utils/tenantStorage';
import { readTenantFromAdminToken } from 'utils/jwtClaims';
import { StoreAdminLoginRequest } from 'types/admin';

interface StoreAdminContextValue {
  isAdminAuthenticated: boolean;
  displayName: string | null;
  login: (data: StoreAdminLoginRequest) => Promise<void>;
  acceptHandoff: (token: string, displayName?: string) => void;
  logout: () => void;
}

const StoreAdminContext = createContext<StoreAdminContextValue | undefined>(undefined);

const syncResolvedTenantFromAdminToken = (token: string): boolean => {
  const claims = readTenantFromAdminToken(token);
  if (!claims) return false;

  const current = getResolvedTenantCode();
  const needsSwitch =
    !current || current.toLowerCase() !== claims.tenantCode.toLowerCase();

  setResolvedTenant({
    tenantId: claims.tenantId,
    tenantCode: claims.tenantCode,
  });

  return needsSwitch;
};

export const StoreAdminProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAdminAuthenticated, setIsAdminAuthenticated] = useState(
    () => storeAdminAuthService.hasSession(),
  );
  const [displayName, setDisplayName] = useState<string | null>(
    () => tenantStorage.getItem(STORAGE_KEYS.ADMIN_DISPLAY_NAME),
  );

  const login = useCallback(async (data: StoreAdminLoginRequest) => {
    const session = await storeAdminAuthService.login(data);
    const needsReload = session.accessToken
      ? syncResolvedTenantFromAdminToken(session.accessToken)
      : false;
    // Re-persistir bajo el tenant_code del JWT (puede diferir del hostname).
    storeAdminAuthService.persistSession(session);
    setIsAdminAuthenticated(true);
    setDisplayName(session.displayName ?? data.username);
    if (needsReload) {
      window.location.replace(ROUTES.ADMIN);
    }
  }, []);

  const acceptHandoff = useCallback((token: string, name?: string) => {
    const needsReload = syncResolvedTenantFromAdminToken(token);
    storeAdminAuthService.acceptHandoffToken(token, name);
    setIsAdminAuthenticated(true);
    setDisplayName(name ?? tenantStorage.getItem(STORAGE_KEYS.ADMIN_DISPLAY_NAME));
    if (needsReload) {
      window.location.replace(ROUTES.ADMIN);
    }
  }, []);

  const logout = useCallback(() => {
    void storeAdminAuthService.logout().finally(() => {
      setIsAdminAuthenticated(false);
      setDisplayName(null);
    });
  }, []);

  const value = useMemo(
    () => ({ isAdminAuthenticated, displayName, login, acceptHandoff, logout }),
    [isAdminAuthenticated, displayName, login, acceptHandoff, logout],
  );

  return <StoreAdminContext.Provider value={value}>{children}</StoreAdminContext.Provider>;
};

export const useStoreAdminContext = () => {
  const ctx = useContext(StoreAdminContext);
  if (!ctx) throw new Error('useStoreAdminContext debe usarse dentro de StoreAdminProvider');
  return ctx;
};
